import { useSyncExternalStore } from "react";

/**
 * The events that INTERRUPT you instead of sliding past in a toast:
 *
 *   • `mention`    — someone @-mentioned you. Always pops, for everyone, with
 *                    no setting (2026-09-20 rule): a mention is a direct
 *                    question, and a toast that auto-dismisses after a few
 *                    seconds is exactly how those get missed.
 *   • `attachment` — a file landed on a lead/deal you are assigned to. The
 *                    server decides this audience (Settings → Manage → "Pop
 *                    assigned users": lead assignees and/or deal assignees)
 *                    and ships the ids on the push as `popupUserIds`.
 *   • `note`       — a note was added on a lead/deal you are on (2026-09-22).
 *                    The server targets the push at the note's audience
 *                    (`note:created`, same people as its inbox row) and the
 *                    org-wide "Pop assigned users → Notes" switch gates it.
 *                    Click-through opens the LEAD, not a chat.
 *
 * Everything else stays a toast.
 */
export type ChatPopupKind = "mention" | "attachment" | "note";

export interface ChatPopupAlert {
  kind: ChatPopupKind;
  /** Logical-message key (`mirrorOfId ?? id`) — the dedupe handle. */
  key: string;
  leadId: number;
  leadName?: string | null;
  /** Deal channel the message lives on (`null` = the lead's All Chats). */
  childId?: number | null;
  /** Message to land on when the popup is clicked through. */
  messageId?: string;
  /** The note (kind `note`) — informational; the click-through opens the lead. */
  noteId?: number;
  senderName: string;
  /** Message text (mention), file name (attachment) or note text (note). */
  preview: string;
  /** "image" | "audio" | "file" — drives the icon on an attachment popup. */
  mediaKind?: "image" | "audio" | "file";
  at: number;
}

/**
 * One popup at a time, the rest queued behind it. A bulk drop (five files in a
 * row, an @all-style burst) must not stack five dialogs on top of each other,
 * and it must not silently swallow the last four either — so they wait, and
 * the dialog says how many are still behind.
 *
 * The queue is capped: past QUEUE_CAP the OLDEST waiting alert is dropped, not
 * the newest. If you are far enough behind to have ten of these stacked up,
 * the useful ones are the recent ones — and none of it is the only record
 * anyway (every one of these is also a toastless inbox row + the thread).
 */
const QUEUE_CAP = 5;

let current: ChatPopupAlert | null = null;
let queue: ChatPopupAlert[] = [];
const listeners = new Set<() => void>();

/** Immutable snapshot — useSyncExternalStore compares by reference. */
let snapshot: { current: ChatPopupAlert | null; waiting: number } = {
  current: null,
  waiting: 0,
};

function publish() {
  snapshot = { current, waiting: queue.length };
  listeners.forEach((fn) => fn());
}

/** Socket handler entry point. */
export function pushChatPopupAlert(alert: ChatPopupAlert) {
  if (!alert?.leadId) return;
  // Same logical message twice (a child message and its All-Chats mirror can
  // both reach us) — one popup.
  if (alert.key) {
    if (current?.key === alert.key) return;
    if (queue.some((a) => a.key === alert.key)) return;
  }

  if (!current) {
    current = alert;
  } else {
    queue.push(alert);
    if (queue.length > QUEUE_CAP) queue = queue.slice(queue.length - QUEUE_CAP);
  }
  publish();
}

/** Close the visible popup and promote whatever was waiting behind it. */
export function dismissChatPopupAlert() {
  current = queue.shift() ?? null;
  publish();
}

/** Drop everything (used when the user clicks through to the chat). */
export function clearChatPopupAlerts() {
  current = null;
  queue = [];
  publish();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => snapshot;

/** Reactive view for ChatPopupAlertHost. */
export function useChatPopupAlert() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test seam. */
export function resetChatPopupAlerts() {
  current = null;
  queue = [];
  snapshot = { current: null, waiting: 0 };
}
